/**
 * PTL Courier Performance — LIVE, segregated by ABD / Non-ABD.
 *
 * The CT delivery-metrics APIs (getDeliveryMetrics/getTATMetrics) carry no
 * shipmentType or appointment dimension, so PTL ABD/Non-ABD performance is NOT
 * derivable from them. Instead we read order-level data via `order/search`
 * (which DOES filter on shipmentType=B2B + appointdeliveryRequired), group by
 * parent courier, and compute performance client-side. No dummy data.
 */
import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {subDays} from 'date-fns'
import {
  Box, Typography, Autocomplete, TextField, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import {apiConstants, postGeneric} from '../../../common/constants'
import {getMerchant} from '../../../helpers/ReduxHelpers'
import {formatDate} from '../../../helpers/UtilityHelper'
import DashboardDateFilter, {DateRangeCaption} from '../../../common/DashboardDateFilter'
import Loader from '../../../common/Loader'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, SUCCESS, WARNING, ERROR} from '../../../styles/foundation/colors'
import {SHADOW} from '../../../styles/foundation/effects'
import {helpTextSx, helpTextTypeSx} from '../../../styles/patterns'

const cardSx = {bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, overflow: 'hidden'}
const statCardSx = (accent) => ({
  flex: '1 1 160px', minWidth: 150, bgcolor: WHITE, borderRadius: '10px', p: '16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, borderTop: `3px solid ${accent}`,
})
const headerCellSx = {
  fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE,
  textTransform: 'uppercase', letterSpacing: '0.03em', py: 1.5, whiteSpace: 'nowrap',
}
const pctColor = (p) => (p >= 90 ? SUCCESS : p >= 75 ? WARNING : ERROR)

// Raw order status → performance bucket.
function bucketOf(status) {
  const s = String(status || '').toUpperCase()
  if (s === 'DELIVERED') return 'delivered'
  if (s.startsWith('RTO')) return 'rto'
  if (s.startsWith('CANCELLED') || s === 'IGNORE_STATUS') return 'cancelled'
  if (['AWB_REGISTERED', 'PICKUP_PENDING', 'OUT_FOR_PICKUP', 'PICKUP_FAILED', 'MANIFESTED'].includes(s)) return 'pickupPending'
  if (['FAILED_DELIVERY', 'DELIVERY_FAILED', 'UNDELIVERED', 'CONTACT_CUSTOMER_CARE'].includes(s)) return 'failed'
  return 'inTransit'
}

const EMPTY = {total: 0, delivered: 0, rto: 0, cancelled: 0, pickupPending: 0, failed: 0, inTransit: 0}

export default function PtlCourierPerformance({abdMode = 'nonabd', view = 'performance'}) {
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''
  const isABD = abdMode === 'abd'

  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState(null)
  const [selected, setSelected] = useState([]) // comparison: selected courier names
  const [dateRange, setDateRange] = useState([{endDate: subDays(new Date(), 1), startDate: subDays(new Date(), 30), key: 'selection'}])

  const fetchOrders = useCallback(async (range) => {
    if (!merchantId) return
    setLoading(true)
    const from = `Date('${formatDate(range[0].startDate, 'yyyy-MM-dd')} 00:00:00')`
    const to = `Date('${formatDate(range[0].endDate, 'yyyy-MM-dd')} 23:59:59')`
    const andfilter = [
      {field: 'shipmentType', operator: 'eq', value: 'B2B'},
      {field: 'merchant.$id', operator: 'eq', value: merchantId},
      {field: 'appointdeliveryRequired', operator: 'eq', value: isABD},
      {field: 'orderSubStatus', operator: 'ne', value: 'UNPROCESSED'},
      {field: 'awbRegisteredDate', operator: 'gte', value: from},
      {field: 'awbRegisteredDate', operator: 'lte', value: to},
    ]
    try {
      const res = await postGeneric(apiConstants.ORDER_LISTS, {
        andfilter, offset: 0, limit: 5000,
        includes: 'orderStatus,appointdeliveryRequired,courierDetail.parent,courierDetail.name',
      })
      // order/search body is { data: [...], count }, and postGeneric returns the axios
      // response — so the rows live at res.data.data.
      const list = res?.data?.data
      setOrders(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('PTL courier performance fetch error:', err)
      setOrders([])
    }
    setLoading(false)
  }, [merchantId, isABD])

  useEffect(() => { fetchOrders(dateRange) }, [fetchOrders]) // eslint-disable-line

  // Group by parent courier and compute performance.
  const rows = useMemo(() => {
    if (!Array.isArray(orders)) return []
    const by = {}
    orders.forEach((o) => {
      const courier = o.courierDetail?.parent || o.courierDetail?.name || 'Unknown'
      if (!by[courier]) by[courier] = {courier, ...EMPTY}
      by[courier].total++
      by[courier][bucketOf(o.orderStatus)]++
    })
    return Object.values(by).map((c) => {
      const net = Math.max(c.total - c.cancelled, 0) // exclude cancelled from rates
      return {
        ...c, net,
        deliveryPct: net ? +(c.delivered / net * 100).toFixed(1) : 0,
        rtoPct: net ? +(c.rto / net * 100).toFixed(1) : 0,
      }
    }).sort((a, b) => b.total - a.total)
  }, [orders])

  const totals = useMemo(() => {
    const t = rows.reduce((a, r) => ({
      total: a.total + r.total, delivered: a.delivered + r.delivered,
      rto: a.rto + r.rto, cancelled: a.cancelled + r.cancelled,
    }), {total: 0, delivered: 0, rto: 0, cancelled: 0})
    const net = Math.max(t.total - t.cancelled, 0)
    return {...t, net, deliveryPct: net ? +(t.delivered / net * 100).toFixed(1) : 0, rtoPct: net ? +(t.rto / net * 100).toFixed(1) : 0}
  }, [rows])

  const label = isABD ? 'ABD' : 'Non-ABD'
  const fetched = Array.isArray(orders)

  const filterBar = (
    <Box sx={{
      bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
      px: 2.5, py: 1.5, mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
    }}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY}}>
          {label} PTL · grouped by parent courier
        </Typography>
        <DateRangeCaption dateRange={dateRange} />
      </Box>
      <DashboardDateFilter dateRange={dateRange} maxDate={subDays(new Date(), 1)} onChange={(r) => { setDateRange(r); fetchOrders(r) }} />
    </Box>
  )

  if (loading) return <>{filterBar}<Loader /></>

  if (fetched && rows.length === 0) {
    return (
      <>
        {filterBar}
        <Box sx={{...cardSx, p: 5, textAlign: 'center'}}>
          <LocalShippingOutlinedIcon sx={{fontSize: 40, color: BORDER, mb: 1}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY}}>No {label} PTL shipments</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.5, maxWidth: 460, mx: 'auto'}}>
            No {label} PTL shipments were booked in the selected date range. Try a wider range.
          </Typography>
        </Box>
      </>
    )
  }

  // ── Comparison view ──
  if (view === 'comparison') {
    const picked = rows.filter((r) => selected.includes(r.courier))
    const metricDefs = [
      {key: 'total', label: 'Total Shipments', fmt: (v) => v.toLocaleString()},
      {key: 'delivered', label: 'Delivered', fmt: (v) => v.toLocaleString()},
      {key: 'deliveryPct', label: 'Delivery %', fmt: (v) => `${v}%`, color: (v) => pctColor(v)},
      {key: 'inTransit', label: 'In Transit', fmt: (v) => v.toLocaleString()},
      {key: 'rto', label: 'RTO', fmt: (v) => v.toLocaleString()},
      {key: 'rtoPct', label: 'RTO %', fmt: (v) => `${v}%`, color: (v) => (v > 5 ? ERROR : SUCCESS)},
      {key: 'pickupPending', label: 'Pickup Pending', fmt: (v) => v.toLocaleString()},
    ]
    return (
      <>
        {filterBar}
        <Box sx={{...cardSx, p: 2.5, mb: 2.5}}>
          <Autocomplete
            multiple options={rows.map((r) => r.courier)} value={selected}
            onChange={(_e, v) => setSelected(v.slice(0, 4))}
            renderTags={(value, getTagProps) => value.map((opt, i) => <Chip label={opt} size='small' {...getTagProps({index: i})} key={opt} />)}
            renderInput={(params) => <TextField {...params} label={`Compare ${label} couriers (up to 4)`} size='small' />}
          />
        </Box>
        {picked.length === 0 ? (
          <Box sx={helpTextSx}>
            <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
            <Typography sx={helpTextTypeSx}>Select up to 4 {label} couriers above to compare them side by side on live metrics.</Typography>
          </Box>
        ) : (
          <TableContainer sx={cardSx}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{bgcolor: '#f8f9fc'}}>
                  <TableCell sx={headerCellSx}>Metric</TableCell>
                  {picked.map((c) => <TableCell key={c.courier} align='right' sx={headerCellSx}>{c.courier}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {metricDefs.map((m) => (
                  <TableRow key={m.key} sx={{'&:hover': {bgcolor: '#fafbfc'}}}>
                    <TableCell sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: SUBTLE}}>{m.label}</TableCell>
                    {picked.map((c) => (
                      <TableCell key={c.courier} align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: m.color ? m.color(c[m.key]) : NAVY}}>
                        {m.fmt(c[m.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
    )
  }

  // ── Performance view ──
  return (
    <>
      {filterBar}
      <Box sx={{display: 'flex', gap: '12px', mb: '20px', flexWrap: 'wrap'}}>
        <Box sx={statCardSx(PRIMARY_BLUE)}>
          <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, color: PRIMARY_BLUE, fontFamily: FONT}}>{totals.total.toLocaleString()}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, fontWeight: WEIGHT.medium}}>Total Shipments</Typography>
        </Box>
        <Box sx={statCardSx(SUCCESS)}>
          <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, color: SUCCESS, fontFamily: FONT}}>{totals.delivered.toLocaleString()}</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, fontWeight: WEIGHT.medium}}>Delivered</Typography>
        </Box>
        <Box sx={statCardSx(pctColor(totals.deliveryPct))}>
          <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, color: pctColor(totals.deliveryPct), fontFamily: FONT}}>{totals.deliveryPct}%</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, fontWeight: WEIGHT.medium}}>Delivery %</Typography>
        </Box>
        <Box sx={statCardSx(ERROR)}>
          <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, color: ERROR, fontFamily: FONT}}>{totals.rtoPct}%</Typography>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, fontWeight: WEIGHT.medium}}>RTO %</Typography>
        </Box>
      </Box>

      <TableContainer sx={cardSx}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{bgcolor: '#f8f9fc'}}>
              {['Courier', 'Shipments', 'Delivered', 'Delivery %', 'In Transit', 'RTO', 'RTO %', 'Pickup Pending'].map((h, i) => (
                <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.courier} sx={{'&:hover': {bgcolor: '#fafbfc'}}}>
                <TableCell sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY}}>{c.courier}</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{c.total.toLocaleString()}</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{c.delivered.toLocaleString()}</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: pctColor(c.deliveryPct)}}>{c.deliveryPct}%</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{c.inTransit.toLocaleString()}</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{c.rto.toLocaleString()}</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: c.rtoPct > 5 ? ERROR : SUCCESS}}>{c.rtoPct}%</TableCell>
                <TableCell align='right' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>{c.pickupPending.toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{...helpTextSx, mt: 2}}>
        <InfoOutlinedIcon sx={{fontSize: 16, color: PRIMARY_BLUE, mt: 0.2}} />
        <Typography sx={helpTextTypeSx}>
          Live {label} PTL performance from order data (shipmentType B2B, appointment delivery {isABD ? 'required' : 'not required'}).
          Delivery % and RTO % are computed over non-cancelled shipments.
          <strong> TAT adherence / first-attempt % are not available for PTL yet</strong> — pending a CT-metrics
          enhancement (shipmentType + ABD dimension). Tracked in docs/APIS_NEEDED.html.
        </Typography>
      </Box>
    </>
  )
}
